// Each panel owns its request and charts. Turbo snapshots retain the HTML but
// never a live request, loading state, chart canvas or document listener.
export class DashboardPanel {
  constructor(element) {
    this.element = element;
    this.content = element.querySelector('[data-panel-content]') || element;
    this.state = this.content.dataset.panelLoaded === 'true' ? 'loaded' : 'idle';
    this.charts = [];
    this.disposed = false;
    this.onToggle = () => {
      if (element.open) {
        this.load();
        this.charts.forEach(chart => chart.resize());
      }
    };
    this.onClick = event => {
      if (event.target.closest('[data-panel-retry]')) this.load();
    };
    element.addEventListener('toggle', this.onToggle);
    element.addEventListener('click', this.onClick);
    if (this.state === 'loaded') this.initializeCharts();
    if (element.tagName !== 'DETAILS' || element.open) this.load();
  }

  async load() {
    if (this.disposed || this.state === 'loading' || this.state === 'loaded') return;
    this.state = 'loading';
    this.request = new AbortController();
    const request = this.request;
    this.content.setAttribute('aria-busy', 'true');
    this.element.setAttribute('aria-busy', 'true');
    this.content.replaceChildren();
    const message = document.createElement('p');
    message.setAttribute('role', 'status');
    message.textContent = `Loading ${this.element.dataset.title.toLowerCase()}…`;
    this.content.appendChild(message);
    try {
      const response = await fetch(this.element.dataset.href, {
        signal: request.signal, credentials: 'same-origin', cache: 'no-store',
        headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' }
      });
      const authentication = response.redirected || response.status === 401;
      if (authentication) {
        this.showError('Your session needs attention. Sign in or complete verification, then reload the dashboard.', true);
        return;
      }
      if (!response.ok || response.headers.get('X-Dashboard-Panel') !== 'true') throw new Error('Panel request failed');
      const html = await response.text();
      if (this.disposed || request.signal.aborted || !this.element.isConnected) return;
      this.content.innerHTML = html;
      // Fragments are server-rendered HTML with inert chart data. Never execute
      // script tags from a response (including a login page or error document).
      this.content.querySelectorAll('script').forEach(script => script.remove());
      this.state = 'loaded';
      this.content.dataset.panelLoaded = 'true';
      this.initializeCharts();
      this.element.querySelector('summary small')?.replaceChildren(document.createTextNode('Loaded'));
      this.element.dispatchEvent(new CustomEvent('dashboard:loaded', { bubbles: true }));
    } catch (error) {
      if (!this.disposed && !request.signal.aborted && this.element.isConnected) {
        this.showError(`${this.element.dataset.title} could not be loaded. The other panels and search are still available.`);
      }
    } finally {
      if (!this.disposed && this.request === request) {
        this.content.setAttribute('aria-busy', 'false');
        this.element.setAttribute('aria-busy', 'false');
      }
    }
  }

  showError(text, authentication = false) {
    if (this.disposed || !this.element.isConnected) return;
    this.state = 'error';
    this.content.replaceChildren();
    const message = document.createElement('p');
    message.setAttribute('role', 'alert');
    message.textContent = text;
    this.content.appendChild(message);
    if (authentication) {
      const link = document.createElement('a');
      link.href = '/';
      link.textContent = 'Sign in / verify session';
      this.content.appendChild(link);
    } else {
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.className = 'ui small button';
      retry.dataset.panelRetry = '';
      retry.textContent = `Retry ${this.element.dataset.title.toLowerCase()}`;
      this.content.appendChild(retry);
    }
  }

  initializeCharts() {
    if (typeof window.echarts === 'undefined') return;
    this.content.querySelectorAll('[data-dashboard-chart]').forEach(element => {
      const chart = window.echarts.init(element);
      this.charts.push(chart);
      const dark = () => document.documentElement.getAttribute('data-color-scheme') === 'dark';
      const update = () => {
        const label = dark() ? '#ccc' : '#333';
        let option;
        if (element.dataset.dashboardChart === 'pie') {
          option = {
            backgroundColor: 'transparent', title: { text: element.dataset.chartTitle, left: 'center', textStyle: { color: label } },
            tooltip: { trigger: 'item', renderMode: 'richText' },
            series: [{ type: 'pie', radius: ['40%', '70%'], center: ['50%', '60%'],
              label: { color: label }, data: JSON.parse(element.dataset.chartSeries) }]
          };
        } else {
          const data = JSON.parse(element.dataset.chartSeries);
          const slow = element.dataset.slowSeconds;
          option = {
            backgroundColor: 'transparent', tooltip: { trigger: 'axis', renderMode: 'richText' },
            legend: { data: ['Daily mean', 'Daily max', `% ≥${slow}s`], textStyle: { color: label } },
            grid: { left: 48, right: 56, top: 40, bottom: 32 },
            xAxis: { type: 'category', data: data.dates, axisLabel: { color: label, formatter: value => value.slice(5) } },
            yAxis: [{ type: 'value', name: 'seconds', min: 0, axisLabel: { color: label } },
              { type: 'value', name: `% ≥${slow}s`, min: 0, max: 100, axisLabel: { color: label } }],
            series: [
              { name: 'Daily mean', type: 'line', data: data.mean, connectNulls: true,
                markLine: { silent: true, symbol: 'none', data: [{ yAxis: Number(slow) }], label: { formatter: `${slow}s` } } },
              { name: 'Daily max', type: 'line', data: data.max, connectNulls: true, lineStyle: { type: 'dashed' } },
              { name: `% ≥${slow}s`, type: 'bar', yAxisIndex: 1, data: data.slowPercent, barMaxWidth: 6 }
            ]
          };
        }
        chart.setOption(option);
      };
      update();
      document.documentElement.addEventListener('uhd:color-scheme-change', update);
      element.dashboardChartCleanup = () => document.documentElement.removeEventListener('uhd:color-scheme-change', update);
    });
    this.onResize = () => this.charts.forEach(chart => chart.resize());
    window.addEventListener('resize', this.onResize);
  }

  dispose() {
    this.disposed = true;
    this.request?.abort();
    this.element.removeEventListener('toggle', this.onToggle);
    this.element.removeEventListener('click', this.onClick);
    if (this.onResize) window.removeEventListener('resize', this.onResize);
    this.content.querySelectorAll('[data-dashboard-chart]').forEach(element => {
      element.dashboardChartCleanup?.();
      delete element.dashboardChartCleanup;
    });
    this.charts.forEach(chart => chart.dispose());
    if (this.state === 'loading') {
      this.content.replaceChildren();
      this.content.dataset.panelLoaded = 'false';
    }
    this.content.setAttribute('aria-busy', 'false');
    this.element.setAttribute('aria-busy', 'false');
  }
}

let panels = [];
function disposePanels() {
  panels.forEach(panel => panel.dispose());
  panels = [];
}
document.addEventListener('turbo:before-visit', disposePanels);
document.addEventListener('turbo:before-cache', disposePanels);
document.addEventListener('turbo:before-render', disposePanels);
document.addEventListener('turbo:load', () => {
  disposePanels();
  // Attention is always recalculated, including when restoring a Turbo snapshot.
  document.querySelectorAll('[data-dashboard-panel]').forEach(element => {
    if (element.tagName !== 'DETAILS') delete element.dataset.panelLoaded;
    panels.push(new DashboardPanel(element));
  });
});
